import React from 'react';
import { PenLine, PenOff } from 'lucide-react';
import type { ExtractedContractDetails, SignatureStatus } from '../../../services/api/contractService';

/** How each signature result is shown. "signed" isn't flagged anywhere. */
export const SIGNATURE_META: Record<Exclude<SignatureStatus, 'signed'>, { label: string; badge: string; notice: string }> = {
  unsigned: {
    label: 'Not signed',
    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800',
    notice: 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800'
  },
  partially_signed: {
    label: 'Partly signed',
    badge: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    notice: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
  },
  unclear: {
    label: 'Signatures unclear',
    badge: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    notice: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-dark-bg-secondary dark:text-gray-300 dark:border-dark-border'
  }
};

export const SignatureBadge: React.FC<{ status?: SignatureStatus; notes?: string }> = ({ status, notes }) => {
  if (!status || status === 'signed') return null;
  const meta = SIGNATURE_META[status];
  return (
    <span
      title={notes}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${meta.badge}`}
    >
      <PenOff className="w-3 h-3" aria-hidden />
      {meta.label}
    </span>
  );
};

const HEADLINES: Record<Exclude<SignatureStatus, 'signed'>, string> = {
  unsigned: "This copy doesn't look signed.",
  partially_signed: 'Not every party seems to have signed this copy.',
  unclear: "We couldn't confirm the signatures on this copy."
};

/**
 * Warns, while adding a contract, when the AI thinks the copy isn't the
 * signed one. Saving is still allowed; the contract is then flagged in the
 * repository until a signed copy replaces it.
 */
export const SignatureNotice: React.FC<{ extracted: ExtractedContractDetails | null }> = ({ extracted }) => {
  if (!extracted) return null;
  if (extracted.signatureStatus === 'signed') {
    return extracted.signatureNotes ? (
      <p className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400">
        <PenLine className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
        {extracted.signatureNotes}
      </p>
    ) : null;
  }
  const status = extracted.signatureStatus;
  return (
    <div role="alert" className={`flex gap-2 p-3 text-sm border rounded-lg ${SIGNATURE_META[status].notice}`}>
      <PenOff className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
      <div>
        <p className="font-medium">{HEADLINES[status]}</p>
        {extracted.signatureNotes && <p className="mt-0.5">{extracted.signatureNotes}</p>}
        <p className="mt-1 opacity-80">
          {status === 'unclear'
            ? 'Check the signature page before saving.'
            : "Upload the signed copy if you have it. If you save this one, it's flagged in the repository."}
        </p>
      </div>
    </div>
  );
};
